if (!customElements.get('popup-section')) {
  class PopupSection extends HTMLElement {
    constructor() {
      super();

      this.cssClasses = {
        visible: 'popup-section--visible',
        bodyNoScroll: 'popup-section-no-scroll',
      };
      this.dismissedStorageKey = 'cc-theme-popup-dismissed';
      this.openTimer = null;
      this.unlockBodyTimer = null;
      this.isOpen = false;

      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleSectionSelect = this.handleSectionSelect.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.closeButton = this.querySelector('.popup-section__close');
      this.modal = this.querySelector('.popup-section__modal');

      this.addEventListener('click', this.handleClick);
      document.addEventListener('shopify:section:select', this.handleSectionSelect);

      if (this.shouldShowPopup()) {
        const delaySeconds = Number(this.dataset.delaySeconds || 0);
        this.openTimer = window.setTimeout(() => this.open(), delaySeconds * 1000);
      }
    }

    disconnectedCallback() {
      this.clearTimers();
      this.removeEventListener('click', this.handleClick);
      document.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('shopify:section:select', this.handleSectionSelect);
      this.unlockBodyScroll();
    }

    get freezeScroll() {
      return this.dataset.freezeScroll === 'true';
    }

    get testMode() {
      return this.dataset.testMode === 'true';
    }

    get dismissForDays() {
      return Number(this.dataset.dismissForDays || 0);
    }

    get popupUrl() {
      return (this.dataset.popupUrl || '').trim();
    }

    shouldShowPopup() {
      if (document.querySelector('.shopify-challenge__container')) return false;
      if (this.testMode) return true;
      if (!this.shouldShowForCurrentUrl()) return false;
      return !this.wasDismissedRecently();
    }

    shouldShowForCurrentUrl() {
      if (!this.popupUrl) return true;

      const currentPage = window.location.href;
      const currentPath = window.location.pathname;
      return this.popupUrl !== currentPage && this.popupUrl !== currentPath;
    }

    wasDismissedRecently() {
      try {
        const lastDismissed = Number(window.localStorage.getItem(this.dismissedStorageKey));
        if (!lastDismissed) return false;

        const dismissedDaysAgo = (Date.now() - lastDismissed) / (1000 * 60 * 60 * 24);
        return dismissedDaysAgo < this.dismissForDays;
      } catch (_error) {
        return false;
      }
    }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;

      this.previouslyActiveElement = document.activeElement;

      if (this.freezeScroll) {
        document.body.classList.add(this.cssClasses.bodyNoScroll);
      }

      this.classList.add(this.cssClasses.visible);
      document.addEventListener('keydown', this.handleKeydown);

      window.setTimeout(() => {
        this.closeButton?.focus();
      }, 500);
    }

    close({ persistDismissal = false } = {}) {
      if (!this.isOpen && !this.classList.contains(this.cssClasses.visible)) return;
      this.isOpen = false;

      this.classList.remove(this.cssClasses.visible);
      document.removeEventListener('keydown', this.handleKeydown);

      if (persistDismissal) this.setDismissed();

      if (this.previouslyActiveElement && typeof this.previouslyActiveElement.focus === 'function') {
        this.previouslyActiveElement.focus();
      }

      if (this.freezeScroll) {
        const transitionDuration = this.getModalTransitionDurationMs();
        this.unlockBodyTimer = window.setTimeout(() => this.unlockBodyScroll(), transitionDuration);
      }
    }

    setDismissed() {
      try {
        window.localStorage.setItem(this.dismissedStorageKey, String(Date.now()));
      } catch (_error) {
        // Ignore storage failures.
      }
    }

    getModalTransitionDurationMs() {
      if (!this.modal) return 500;

      const styles = window.getComputedStyle(this.modal);
      const durations = styles.transitionDuration.split(',').map((value) => this.parseTimeToMs(value.trim()));
      const delays = styles.transitionDelay.split(',').map((value) => this.parseTimeToMs(value.trim()));
      const totals = durations.map((duration, index) => duration + (delays[index] || 0));

      return Math.max(...totals, 500);
    }

    parseTimeToMs(value) {
      if (!value) return 0;
      if (value.endsWith('ms')) return parseFloat(value) || 0;
      if (value.endsWith('s')) return (parseFloat(value) || 0) * 1000;
      return 0;
    }

    unlockBodyScroll() {
      document.body.classList.remove(this.cssClasses.bodyNoScroll);
    }

    clearTimers() {
      if (this.openTimer) window.clearTimeout(this.openTimer);
      if (this.unlockBodyTimer) window.clearTimeout(this.unlockBodyTimer);
      this.openTimer = null;
      this.unlockBodyTimer = null;
    }

    handleClick(event) {
      if (!event.target.closest('.popup-section__close, .popup-section__background')) return;
      this.close({ persistDismissal: true });
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.close({ persistDismissal: true });
      }
    }

    handleSectionSelect(event) {
      if (!window.Shopify?.designMode) return;
      if (event.detail?.sectionId && event.detail.sectionId === this.dataset.sectionId) {
        this.clearTimers();
        this.open();
      }
    }
  }

  customElements.define('popup-section', PopupSection);
}
